"use client";

import { Check, AlertCircle, LoaderCircle } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export type UploadStepStatus = "pending" | "active" | "processing" | "complete" | "error";

export interface UploadStep {
  id: 1 | 2 | 3;
  label: string;
  description?: string;
  status: UploadStepStatus;
}

export interface UploadStepperProps {
  steps: UploadStep[];
  className?: string;
}

export function UploadStepper({ steps, className }: UploadStepperProps) {
  return (
    <nav
      aria-label="Workflow progress"
      className={cn("w-full", className)}
      data-testid="upload-stepper"
    >
      <ol className="flex items-center gap-2 sm:gap-3">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          return (
            <li
              key={step.id}
              className={cn("flex items-center", !isLast && "flex-1 min-w-0")}
              aria-current={step.status === "active" || step.status === "processing" ? "step" : undefined}
              data-testid={`upload-stepper-step-${step.id}`}
              data-status={step.status}
            >
              <div className="flex items-center gap-2 min-w-0">
                <StepIndicator status={step.status} stepNumber={step.id} />
                <span
                  className={cn(
                    "text-xs sm:text-sm font-medium leading-none truncate transition-colors",
                    step.status === "pending" && "text-muted-foreground",
                    (step.status === "active" || step.status === "processing") && "text-foreground",
                    step.status === "complete" && "text-foreground/80",
                    step.status === "error" && "text-destructive",
                  )}
                >
                  {step.label}
                </span>
              </div>

              {!isLast && (
                <div
                  className={cn(
                    "flex-1 mx-2 sm:mx-3 h-px transition-colors",
                    step.status === "complete" ? "bg-success/60" : "bg-border",
                  )}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function StepIndicator({
  status,
  stepNumber,
}: {
  status: UploadStepStatus;
  stepNumber: number;
}) {
  return (
    <div
      className={cn(
        "flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full text-[10px] sm:text-xs font-semibold transition-all shrink-0",
        status === "pending" && "border border-border bg-background text-muted-foreground",
        status === "active" && "bg-primary text-primary-foreground ring-2 ring-primary/20",
        status === "processing" && "bg-primary text-primary-foreground",
        status === "complete" && "bg-success text-success-foreground",
        status === "error" && "bg-destructive text-destructive-foreground",
      )}
      aria-hidden="true"
    >
      {status === "complete" ? (
        <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5" strokeWidth={3} />
      ) : status === "error" ? (
        <AlertCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
      ) : status === "processing" ? (
        <LoaderCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 animate-spin" />
      ) : (
        <span>{stepNumber}</span>
      )}
    </div>
  );
}
